import React from 'react';
import { createPortal } from 'react-dom';
import { CommitmentConflict } from 'utils/commitmentConflicts';
import './CommitmentConflictDialog.css';

interface CommitmentConflictDialogProps {
  isOpen: boolean;
  conflicts: CommitmentConflict[];
  /** Disables the cancel-offer action while the cancellation runs */
  isCancellingOffer?: boolean;
  /** Option 1: abort this trade/offer attempt */
  onAbort: () => void;
  /** Option 2: cancel the conflicting pending offer(s), then the caller retries */
  onCancelConflictingOffers: () => void;
  /** Option 3: the have list is wrong — route to the quantity editor */
  onEditHaveList: () => void;
}

/**
 * Fires only on a real shortfall (committed + new commitment > owned) — spare
 * copies never trigger it. There is deliberately no unverified "I have enough"
 * bypass: a user who owns more copies says so in the have list, the place the
 * system reads from.
 */
const CommitmentConflictDialog: React.FC<CommitmentConflictDialogProps> = ({
  isOpen,
  conflicts,
  isCancellingOffer = false,
  onAbort,
  onCancelConflictingOffers,
  onEditHaveList,
}) => {
  if (!isOpen || conflicts.length === 0) return null;

  const offerCount = new Set(conflicts.flatMap((c) => c.offerIds)).size;
  const plural = conflicts.length > 1;

  return createPortal(
    <div className="commit-conflict-overlay" role="presentation" onClick={onAbort}>
      <div
        className="commit-conflict-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="commit-conflict-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="commit-conflict-title" className="commit-conflict-title">
          {plural ? 'Cards already committed' : 'Card already committed'}
        </h2>
        <p className="commit-conflict-body">
          {plural ? 'Some cards in this trade are' : 'A card in this trade is'} already committed
          to an open trade offer, and you don&apos;t have enough spare copies to cover both.
        </p>
        <ul className="commit-conflict-list">
          {conflicts.map((c) => (
            <li key={`${c.cardName}-${c.userMTGCardHaveId ?? c.offerIds.join('-')}`}>
              <strong>{c.cardName}</strong>: you own {c.ownedQuantity},{' '}
              {c.committedQuantity} already committed to{' '}
              {c.offerIds.length === 1 ? 'an open offer' : `${c.offerIds.length} open offers`}, and
              this trade needs {c.requestedQuantity} more.
            </li>
          ))}
        </ul>
        <div className="commit-conflict-actions">
          <button type="button" className="commit-conflict-btn" onClick={onAbort}>
            Cancel this trade
          </button>
          <button
            type="button"
            className="commit-conflict-btn"
            onClick={onCancelConflictingOffers}
            disabled={isCancellingOffer}
          >
            {isCancellingOffer
              ? 'Cancelling…'
              : offerCount === 1
                ? 'Cancel that offer instead'
                : 'Cancel those offers instead'}
          </button>
          <button type="button" className="commit-conflict-btn" onClick={onEditHaveList}>
            I have more copies than my have list shows
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CommitmentConflictDialog;
